import { useZnglyContext } from '@/Utility/context/ZnglyAppContext';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

interface CategoryEdge {
  id: string;
  name: string;
  parent?: { id: string } | null;
  children?: {
    edges: Array<{ id: string; name: string }>;
  } | null;
}

export interface CategoryDropdownProps {
  className?: string;
  selectedCategories: string[];
  onCategoriesChange: (categories: string[]) => void;
  displayName?: string;
  variant?: 'default' | 'compact';
}

const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  className,
  selectedCategories = [],
  onCategoriesChange,
  displayName = 'Categories',
  variant = 'compact',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const { appData } = useZnglyContext();
  const categories = appData.categories;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleExpand = (categoryId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpanded((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Improved selection logic with proper typing
  const handleSelectCategory = useCallback(
    (categoryId: string, parentId?: string, children?: Array<{ id: string; name: string }>) => {
      let newSelected = [...selectedCategories];
      const isSelected = newSelected.includes(categoryId);

      if (isSelected) {
        // Remove this category
        newSelected = newSelected.filter((id) => id !== categoryId);

        // If deselecting a parent, deselect all children
        if (children?.length) {
          children.forEach((child) => {
            newSelected = newSelected.filter((id) => id !== child.id);
          });
        }

        // If deselecting a child, ensure parent is also deselected if it was selected
        if (parentId && newSelected.includes(parentId)) {
          newSelected = newSelected.filter((id) => id !== parentId);
        }
      } else {
        // Add this category
        newSelected.push(categoryId);

        // If selecting a parent, select all children
        if (children?.length) {
          children.forEach((child) => {
            if (!newSelected.includes(child.id)) {
              newSelected.push(child.id);
            }
          });
        }

        // If selecting a child, check if all siblings are now selected to select the parent
        if (parentId) {
          const parent = categories.edges.find((cat) => cat.id === parentId);
          if (parent?.children?.edges) {
            const allChildrenSelected = parent.children.edges.every(
              (child) => newSelected.includes(child.id) || child.id === categoryId
            );
            if (allChildrenSelected && !newSelected.includes(parentId)) {
              newSelected.push(parentId);
            }
          }
        }
      }

      onCategoriesChange(newSelected);
    },
    [selectedCategories, categories.edges, onCategoriesChange]
  );

  // Auto-expand categories with selected children
  useEffect(() => {
    if (selectedCategories.length > 0 && categories.edges) {
      const newExpanded: Record<string, boolean> = {};

      categories.edges.forEach((category) => {
        if (!category.parent && category.children?.edges) {
          const hasSelectedChildren = category.children.edges.some((child) => selectedCategories.includes(child.id));
          if (hasSelectedChildren || selectedCategories.includes(category.id)) {
            newExpanded[category.id] = true;
          }
        }
      });

      setExpanded((prev) => ({ ...prev, ...newExpanded }));
    }
  }, [selectedCategories, categories.edges]);

  const isPartiallySelected = useCallback(
    (category: CategoryEdge) => {
      if (!category.children?.edges?.length) return false;

      const selectedChildrenCount = category.children.edges.filter((child) =>
        selectedCategories.includes(child.id)
      ).length;
      return selectedChildrenCount > 0 && selectedChildrenCount < category.children.edges.length;
    },
    [selectedCategories]
  );

  const menuContent = useMemo(() => {
    if (!categories.edges || categories.edges.length === 0) {
      return <CircularProgress size={24} />;
    }

    const topLevelCategories = categories.edges.filter(
      (cat) => !cat.parent && cat.name.toLowerCase() !== 'uncategorized'
    );

    return (
      <List sx={{ width: '100%', maxWidth: 360, maxHeight: 400, overflow: 'auto' }}>
        {topLevelCategories.map((category) => {
          const isSelected = selectedCategories.includes(category.id);
          const isIndeterminate = isPartiallySelected(category);
          const isExpanded = expanded[category.id];
          const hasChildren = category.children?.edges && category.children.edges.length > 0;

          return (
            <React.Fragment key={category.id}>
              <ListItem
                disablePadding
                secondaryAction={
                  hasChildren ? (
                    <Button onClick={(e) => toggleExpand(category.id, e)} sx={{ minWidth: 40 }}>
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Button>
                  ) : null
                }
              >
                <ListItemButton onClick={() => handleSelectCategory(category.id, undefined, category.children?.edges)}>
                  <ListItemIcon>
                    <Checkbox edge="start" checked={isSelected} indeterminate={isIndeterminate} className="zn-chbx-1" />
                  </ListItemIcon>
                  <ListItemText primary={category.name} />
                </ListItemButton>
              </ListItem>

              {hasChildren && isExpanded && (
                <List component="div" disablePadding>
                  {category.children?.edges.map((subcat) => (
                    <ListItem key={subcat.id} disablePadding sx={{ pl: 4 }}>
                      <ListItemButton onClick={() => handleSelectCategory(subcat.id, category.id)}>
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedCategories.includes(subcat.id)}
                            className="zn-chbx-1"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={subcat.name}
                          primaryTypographyProps={{
                            style: { fontSize: '0.9rem' },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
              <Divider component="li" />
            </React.Fragment>
          );
        })}

        {selectedCategories.length > 0 && (
          <ListItem sx={{ p: 1 }}>
            <Button fullWidth size="small" color="error" onClick={() => onCategoriesChange([])}>
              Clear Selection
            </Button>
          </ListItem>
        )}
      </List>
    );
  }, [categories.edges, selectedCategories, expanded, isPartiallySelected, handleSelectCategory]);

  const buttonClass = variant === 'default' ? 'z-btn-6' : 'z-btn-compact';

  return (
    <>
      <Tooltip title="Select categories">
        <Button
          className={`${buttonClass} ${className}`}
          onClick={handleClick}
          endIcon={<ArrowDropDownIcon />}
          sx={{ position: 'relative' }}
        >
          {displayName}
          {selectedCategories.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -5,
                right: -5,
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              {selectedCategories.length}
            </Box>
          )}
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 500,
            width: '300px',
            mt: 1,
            boxShadow: 3,
          },
        }}
      >
        {menuContent}
      </Menu>
    </>
  );
};

export default CategoryDropdown;
